import { CSSResultGroup, html, LitElement, nothing, TemplateResult } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { Navigation, NavigationItem } from 'components/discovery/navigation-menu/typing/navigation-types.js'
import { style } from './navigation-menu-column.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { BaseUrlController } from 'components/shared/enterprise-domains/base-url-controller.js'

initializeIconsByRole({
  'csc-navigation-menu-column.arrow': { name: 'angle-right', style: 'regular' },
  'csc-navigation-menu-column.more': { name: 'angles-down', style: 'regular' }
})

export type AllProductsLinkPosition = 'top' | 'bottom'

@customElement('csc-navigation-menu-column')
export class CSCNavigationMenuColumn extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  private urls = new BaseUrlController(this)

  @property({ type: Array, attribute: 'navigation-items' })
  navigationItems: Navigation = []

  @property({ attribute: false })
  parentItem: NavigationItem | null = null

  @property({ type: Number, attribute: 'active-item-id' })
  activeItemId: number | null = null

  @property({ type: Boolean, attribute: 'is-sub-level' })
  isSubLevel: boolean = false

  @property({ type: Boolean, attribute: 'show-asset' })
  showAsset: boolean = false

  @property({ type: String, attribute: 'asset-size' })
  assetSize: string = '24'

  @property({ type: String, attribute: 'asset-width' })
  assetWidth?: string

  @property({ type: String, attribute: 'asset-height' })
  assetHeight?: string

  @property({ type: Boolean })
  dividers: boolean = false

  @property({ type: Boolean, attribute: 'show-title' })
  showTitle: boolean = false

  @property({ type: Boolean, attribute: 'show-title-sub-level' })
  showTitleSubLevel: boolean = false

  @property({ type: String, attribute: 'all-products-link-position' })
  allProductsLinkPosition: AllProductsLinkPosition = 'top'

  @property({ type: Boolean, attribute: 'include-cateogory-name' })
  includeCategoryName: boolean = false

  @property({ type: String, attribute: 'default-image' })
  defaultImage: string = ''

  @state()
  private showMoreIndicator = false

  @state()
  private atScrollBottom = false

  private resizeObserver: ResizeObserver | undefined

  private scrollListener: ((e: Event) => void) | undefined

  override firstUpdated(): void {
    this.updateComplete.then(() => {
      this.checkOverflow()
      this.setupResizeObserver()
      this.setupScrollListener()
    })
  }

  override updated(changedProperties: Map<string | number | symbol, unknown>): void {
    if (changedProperties.has('navigationItems')) {
      this.updateComplete.then(() => {
        this.checkOverflow()
        this.checkScrollPosition()
      })
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.resizeObserver?.disconnect()
    this.removeScrollListener()
  }

  private dispatchItemSelect(item: NavigationItem): void {
    if (this.activeItemId !== item.id) {
      this.dispatchEvent(new CustomEvent('item-select', {
        detail: item,
        bubbles: true,
        composed: true
      }))
    }
  }

  private checkOverflow(): void {
    const list = this.shadowRoot?.querySelector('.list')
    if (list) {
      const tolerance = 1
      this.showMoreIndicator = list.scrollHeight > (list.clientHeight + tolerance)
      if (this.showMoreIndicator) {
        this.checkScrollPosition()
      } else {
        this.atScrollBottom = true
      }
    } else {
      this.showMoreIndicator = false
      this.atScrollBottom = true
    }
  }

  private setupResizeObserver(): void {
    const list = this.shadowRoot?.querySelector('.list')
    if (list) {
      this.resizeObserver = new ResizeObserver(() => {
        this.checkOverflow()
        this.checkScrollPosition()
      })
      this.resizeObserver.observe(list)
    }
  }

  private setupScrollListener(): void {
    const list = this.shadowRoot?.querySelector('.list')
    if (list) {
      this.scrollListener = this.handleScroll.bind(this)
      list.addEventListener('scroll', this.scrollListener)
    }
  }

  private removeScrollListener(): void {
    const list = this.shadowRoot?.querySelector('.list')
    if (list && this.scrollListener) {
      list.removeEventListener('scroll', this.scrollListener)
    }
  }

  private handleScroll(event: Event): void {
    this.checkScrollPosition(event.target as HTMLElement)
  }

  private checkScrollPosition(targetElement?: HTMLElement): void {
    const list = targetElement || this.shadowRoot?.querySelector('.list')
    if (list && this.showMoreIndicator) {
      const { scrollTop, scrollHeight, clientHeight } = list
      const scrollTolerance = 5
      this.atScrollBottom = (scrollHeight - scrollTop - clientHeight) <= scrollTolerance
    } else if (!this.showMoreIndicator) {
      this.atScrollBottom = true
    }
  }

  private scrollToBottom(): void {
    const list = this.shadowRoot?.querySelector('.list')
    if (list) {
      list.scrollTop = list.scrollHeight - list.clientHeight
      this.checkScrollPosition(list as HTMLElement)
    }
  }

  private renderAllProductsLink(): TemplateResult | typeof nothing {
    if (!this.parentItem) {
      return nothing
    }

    const { slug, id, name } = this.parentItem
    const url = this.urls.path(`${slug}/${id}/`)

    return html`
      <div class="all-products-link-item">
        <cse-link url="${url}" variant="primary">
          ${t('all_products_in_category', { ns: 'csc-navigation-menu-column', default: 'See all products' })}
          ${this.includeCategoryName ? name : nothing }
          <cse-icon slot="suffix" icon-role="csc-navigation-menu-column.arrow"></cse-icon>
        </cse-link>
      </div>
    `
  }

  private renderTitle(): TemplateResult | typeof nothing {
    if (this.isSubLevel && this.navigationItems && this.navigationItems.length > 0) {
      const firstItem = this.navigationItems[0]

      if (!firstItem || !firstItem.parent_name || !this.showTitleSubLevel) {
        return nothing
      }

      const parentCategoryName = firstItem.parent_name
      return html`
        <div class="title-item">
          <cse-text as="p" variant="body" class="title">
            ${parentCategoryName}
          </cse-text>
        </div>
      `
    } else if (!this.isSubLevel && this.showTitle) {
      return html`
        <div class="title-item">
          <cse-text as="p" variant="body" class="title">
            ${t('all_categories', { ns: 'csc-navigation-menu-column', default: 'All categories' })}
          </cse-text>
        </div>
      `
    }
    return nothing
  }

  override render(): TemplateResult {
    const showVisuals = this.showMoreIndicator && !this.atScrollBottom
    const listClass = `list ${showVisuals ? 'show-gradient' : ''}`
    const containerClass = `container ${this.isSubLevel ? 'sub-level' : ''}`

    const navigationItemsTemplate = this.navigationItems.filter(item => !item.hidden).map(item => {
      const url = this.urls.path(`${item.slug}/${item.id}/`)
      return html`
      <cse-list ?divider="${this.dividers}">
        <cse-list-item>
          <cse-menu-item
            class="nav-item"
            ?selected="${this.activeItemId === item.id}"
            @mouseenter="${() => this.dispatchItemSelect(item)}"
            @focusin="${() => this.dispatchItemSelect(item)}"
            url="${url}"
          >
            ${this.showAsset && (item.icon_role)
              ? html`
                <cse-image
                  source="${item.icon_role}"
                  loading="lazy"
                  fallback-source="${this.defaultImage}"
                  width="${this.assetWidth ?? this.assetSize}"
                  height="${this.assetHeight ?? this.assetSize}"
                  slot="prefix"
                ></cse-image>
              ` : nothing
            }
            <cse-text as="p" variant="body" class="category-name">
              ${this.isSubLevel ? item.name : item.name}
            </cse-text>
            ${item.childs && item.childs.length > 0
              ? html`<cse-icon slot="suffix" icon-role="csc-navigation-menu-column.arrow" class="arrow-icon"></cse-icon>`
              : nothing
            }
          </cse-menu-item>
        </cse-list-item>
      </cse-list>
    `})

    return html`
      <div class="${containerClass}">
        <div class="${listClass}">
          ${this.renderTitle()}
          ${this.allProductsLinkPosition === 'top' ?
            this.renderAllProductsLink() :
            nothing
          }
          ${navigationItemsTemplate}
          ${this.allProductsLinkPosition === 'bottom' ?
            this.renderAllProductsLink() :
            nothing
          }
        </div>
        <cse-chip class="more-indicator" ?hidden="${!showVisuals}" @click="${this.scrollToBottom}">
          ${t('more', { ns: 'csc-navigation-menu-column', default: 'More' })}
          <cse-icon icon-role="csc-navigation-menu-column.more" slot="prefix"></cse-icon>
        </cse-chip>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-navigation-menu-column': CSCNavigationMenuColumn
  }
}
